import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Navigation, LocateFixed, BookmarkCheck, Printer, Phone, AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";
import { api, errMsg, API_BASE, pollJob, download } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const myLocationUrl = (er) => er.my_location_url || (er.hospital?.lat ? `https://www.google.com/maps/dir/?api=1&destination=${er.hospital.lat},${er.hospital.lon}&travelmode=driving` : er.directions_url);

export const DirectionsButtons = ({ er, doc }) => (
  <div className="flex flex-wrap gap-2">
    <a href={myLocationUrl(er)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-alert px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" data-testid="route-my-location"><LocateFixed className="h-4 w-4" />Directions to A&E from my location</a>
    <a href={er.directions_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium hover:bg-accent" data-testid="route-directions"><Navigation className="h-4 w-4" />From site in Google Maps</a>
    {doc && <button type="button" onClick={() => download(`/documents/${doc.id}/emergency-poster`, `${doc.doc_number}_emergency_poster.pdf`).catch((e) => toast.error(errMsg(e)))} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium hover:bg-accent" data-testid="route-poster"><Printer className="h-4 w-4" />A4 site poster</button>}
  </div>
);

const Details = ({ doc, er, v }) => (
  <div className="mt-3 grid gap-4 md:grid-cols-5">
    <dl className="space-y-1.5 text-sm md:col-span-2">{[["Hospital", er.hospital.name], ["Address", er.hospital.address || "—"], ["Phone", er.hospital.phone || "—"], ["Distance", `${(er.distance_m / 1609.34).toFixed(1)} miles${er.duration_s ? ` · about ${Math.round(er.duration_s / 60)} min drive` : ""}`]].map(([k, val]) => <div key={k}><dt className="text-xs text-muted-foreground">{k}</dt><dd className="font-medium" data-testid={`route-${k.toLowerCase()}`}>{val}</dd></div>)}
      <p className="text-xs text-muted-foreground">{er.from_saved ? "Saved hospital for this site" : er.auto_found ? "Found automatically from OpenStreetMap" : "Hospital chosen by you"} · {er.generated_by}. Confirm before works start.</p></dl>
    {er.has_map !== false ? <a href={myLocationUrl(er)} target="_blank" rel="noreferrer" className="md:col-span-3" title="Open directions in Google Maps" data-testid="route-map-link">
      <img src={`${API_BASE}/documents/${doc.id}/emergency-route/map?v=${v || er.generated_at}`} alt="Route from site to nearest A&E" className="w-full rounded-xl border transition-opacity hover:opacity-90" data-testid="route-map" />
    </a> : <p className="text-xs text-muted-foreground md:col-span-3" data-testid="route-no-map">Map image unavailable right now — directions links still work. Use "Find nearest again" later to add the map.</p>}
  </div>
);

const AeFacts = ({ er }) => {
  const h = er.hospital;
  return (
    <dl className="space-y-1.5 text-sm">
      <div><dt className="text-xs text-muted-foreground">Nearest A&E</dt><dd className="font-semibold" data-testid="ae-card-name">{h.name}</dd></div>
      <div><dt className="text-xs text-muted-foreground">Address</dt><dd className="font-medium" data-testid="ae-card-address">{h.address || "—"}</dd></div>
      <div><dt className="text-xs text-muted-foreground">Phone</dt><dd className="font-medium">{h.phone ? <a href={`tel:${h.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 text-alert hover:underline" data-testid="ae-card-phone"><Phone className="h-3.5 w-3.5" />{h.phone}</a> : "—"}</dd></div>
      <div><dt className="text-xs text-muted-foreground">Distance</dt><dd className="font-medium" data-testid="ae-card-distance">{er.distance_m != null ? `${(er.distance_m / 1609.34).toFixed(1)} miles${er.duration_s ? ` · about ${Math.round(er.duration_s / 60)} min drive` : ""}` : "—"}</dd></div>
    </dl>
  );
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null);

export const AeStatus = ({ doc, reload, testid }) => {
  const [busy, setBusy] = useState(false);
  const s = doc.emergency_route?.status;
  if (!s || s.state === "unchecked") return null;
  const recheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/emergency-route/check`, { force: true });
      const st = data.status;
      if (!st.service_ok) toast.error(st.message);
      else if (st.state === "ok") toast.success("A&E confirmed — still listed as an emergency department");
      else toast.warning(st.message);
      reload?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const btn = <button type="button" onClick={recheck} disabled={busy} className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold hover:bg-accent disabled:opacity-60" data-testid={`${testid}-recheck`}><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />{busy ? "Checking…" : "Re-check A&E"}</button>;
  if (s.state === "ok") {
    return <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-health" data-testid={testid} data-state="ok"><ShieldCheck className="h-4 w-4" />Still listed as an A&E · checked {fmtDate(s.checked_at)}{btn}</div>;
  }
  const strong = s.state === "changed";
  return (
    <div className={`mt-3 flex flex-wrap items-start gap-3 rounded-xl border p-3 text-sm ${strong ? "border-alert/50 bg-alert/10" : "border-[#FFA000]/40 bg-[#FFA000]/10"}`} data-testid={testid} data-state={s.state}>
      <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${strong ? "text-alert" : "text-[#B87300]"}`} />
      <div className="min-w-0 flex-1"><p className="font-semibold">{strong ? "This A&E may have changed or closed" : s.state === "stale" ? "A&E needs re-confirming" : "A&E not confirmed"}</p>
        <p className="text-muted-foreground" data-testid={`${testid}-message`}>{s.message}{s.confirmed_at ? ` Last confirmed ${fmtDate(s.confirmed_at)}.` : ""}</p></div>
      {btn}
    </div>
  );
};

export const AeCard = ({ doc, reload }) => {
  const er = doc.emergency_route;
  if (!er) {
    return (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-alert/30 bg-alert/5 p-4 text-sm" data-testid="ae-card-missing">
        <span className="flex items-center gap-2 font-medium"><MapPin className="h-4 w-4 text-alert" />A&E to be confirmed — {doc.nearest_ae || "identify the nearest A&E before works start"}</span>
        <button type="button" onClick={() => document.querySelector('[data-testid="emergency-route"]')?.scrollIntoView({ behavior: "smooth", block: "center" })} className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-accent" data-testid="ae-card-add-route">Add A&E route</button>
      </div>
    );
  }
  return (
    <div className="mb-4 rounded-xl border border-alert/30 bg-alert/5 p-4" data-testid="ae-card">
      <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-alert"><MapPin className="h-4 w-4" />Emergency hospital (A&E)</p>
      <div className="grid gap-4 md:grid-cols-5">
        <div className="space-y-3 md:col-span-2"><AeFacts er={er} /><DirectionsButtons er={er} /></div>
        {er.has_map !== false && <a href={myLocationUrl(er)} target="_blank" rel="noreferrer" className="md:col-span-3" title="Open directions in Google Maps" data-testid="ae-card-map-link">
          <img src={`${API_BASE}/documents/${doc.id}/emergency-route/map?v=${er.generated_at}`} alt="Route from site to nearest A&E" className="w-full rounded-xl border transition-opacity hover:opacity-90" data-testid="ae-card-map" />
        </a>}
      </div>
      <AeStatus doc={doc} reload={reload} testid="ae-card-status" />
    </div>
  );
};

export const EmergencyRoute = ({ doc, reload }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  const [saved, setSaved] = useState(null);
  const er = doc.emergency_route;
  useEffect(() => {
    if (doc.site_address && !er) {
      api.get("/site-hospitals/match", { params: { address: doc.site_address } }).then((r) => setSaved(r.data.match)).catch(() => {});
    }
  }, [doc.site_address, er]);
  const routeAt = er?.generated_at;
  useEffect(() => {
    if (routeAt) {
      api.post(`/documents/${doc.id}/emergency-route/check`, { force: false }).then((r) => r.data.changed && reload()).catch(() => {});
    }
  }, [doc.id, routeAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/emergency-route`, body);
      await pollJob(data.job_id);
      toast.success("Route to A&E added and saved for this site — check it before works start");
      setV(Date.now());
      reload();
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
    }
    setBusy(false);
  };
  const can = doc.can?.["doc.edit"];
  return (
    <div className="rounded-2xl border bg-card p-5" data-testid="emergency-route">
      <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="flex items-center gap-2 font-semibold"><MapPin className="h-4 w-4 text-alert" />Nearest A&E & route</h2>
        {er && <DirectionsButtons er={er} doc={doc} />}</div>
      {er ? <><Details doc={doc} er={er} v={v} /><AeStatus doc={doc} reload={reload} testid="route-status" /></> : <p className="mt-2 text-sm text-muted-foreground">Add a map and driving route from the site address to the nearest A&E department. It appears in the Emergency arrangements section of the PDF and is remembered for this site.</p>}
      {!er && saved && <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-health/40 bg-health/10 p-3 text-sm" data-testid="saved-hospital-hint"><BookmarkCheck className="h-4 w-4 text-health" /><span>Saved for this site: <b>{saved.hospital.name}</b></span>{can && <Button size="sm" disabled={busy} onClick={() => run({})} data-testid="route-use-saved">{busy ? "Adding…" : "Use saved hospital"}</Button>}</div>}
      {can && <div className="mt-4 flex flex-wrap gap-2 border-t pt-4">
        <Button size="sm" variant={saved && !er ? "outline" : "default"} onClick={() => run({ refresh: true })} disabled={busy || !doc.site_address} data-testid="route-auto">{busy ? "Finding route…" : er ? "Find nearest again" : "Find nearest A&E"}</Button>
        <Input className="max-w-xs" placeholder="Or enter a hospital name and town" value={q} onChange={(e) => setQ(e.target.value)} data-testid="route-hospital-input" />
        <Button size="sm" variant="outline" disabled={busy || q.length < 4 || !doc.site_address} onClick={() => run({ hospital_query: q })} data-testid="route-manual">Use this hospital</Button>
        {!doc.site_address && <p className="w-full text-xs text-alert">Add the site address above first.</p>}
      </div>}
    </div>
  );
};
